import { Component, Input, OnInit, effect, inject, OnDestroy } from '@angular/core';
import { AuthService } from '../../services/auth.service';

import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from '../../services/local-storage.service';
import { SessionVariable } from '../../enum/session-variable';
import { MessageService } from 'primeng/api';
import { SharedSignalsService } from '../../services/shared-signals.service';
import { FormControl, Validators } from '@angular/forms';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { CheckoutSignalsService } from '../../../checkout/services/checkout-signals.service';
import { CheckoutService } from '../../../checkout/services/checkout.service';
import { CnetAuthSessionService } from '../../../checkout/services/cnet-auth-session.service';
import { TranslationService } from '../../services/translation.service';
import {
  MPGS_CARD_NOT_CAPTURED,
  GATEWAY_TOKEN_MISSING_CODES,
  MPGS_CARD_REGISTRATION_FAILED,
  MPGS_CARD_COMPLETION_IN_PROGRESS,
} from '../../../checkout/checkout-model';

const COMPLETION_IN_PROGRESS_RETRIES = 3;
const COMPLETION_IN_PROGRESS_DELAY_MS = 3000;

@Component({
  selector: 'taly-verify-payment',
  templateUrl: './verify-payment.component.html',
  styleUrls: ['./verify-payment.component.scss'],
  providers: [CheckoutService],
  standalone: false,
})
export class VerifyPaymentComponent implements OnInit, OnDestroy {
  transferSignal = inject(SharedSignalsService);
  private authService = inject(AuthService);
  private checkoutService = inject(CheckoutService);
  private toasterService = inject(MessageService);
  private router = inject(Router);
  private storageService = inject(LocalStorageService);
  private activatedRoute = inject(ActivatedRoute);
  private loaderService = inject(NgxUiLoaderService);
  transfer = inject(CheckoutSignalsService);
  private readonly cnetAuthSessionService = inject(CnetAuthSessionService);
  private readonly translationService = inject(TranslationService);

  @Input({ required: true }) paymentType: string;
  @Input({ required: true }) key: string;
  @Input() tap_id: string;
  @Input() orderId: string;
  @Input({ required: true }) status: string;
  resultIndicator: any;
  paymentId: string;
  @Input() sessionVersion: string;
  @Input() checkoutVersion: string;
  interval: ReturnType<typeof setInterval> | null = null;
  message: string = 'PROCESSING';
  isSuccess: boolean = false;
  intervalId: any;
  timeoutId: any;
  cNetResponseStatus: any;
  ordersInfo: any;
  textDirection: any;
  errorMessage: any;
  cardId: any;
  private completionRetries = 0;
  private completionRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  paymentMethod = new FormControl(null, [Validators.required]);

  constructor() {
    effect(() => {
      this.ordersInfo = this.transfer.orderDetails();
      this.textDirection = this.transferSignal?.textDirection();
    });
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.resultIndicator = params['resultIndicator'];
      this.paymentId = params['paymentId'];
      if ((!this.resultIndicator || this.resultIndicator === 'undefined') && this.paymentId) {
        this.resultIndicator = this.paymentId;
      }
    });
    this.transferSignal.setFooter(false);
    this.transferSignal.setHeader(false);
    this.transferSignal.setDarkHeader(false);
    this.transferSignal.headerType.set(undefined);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.orderId = this.transfer.resolveOrderId();
    if (this.paymentType == 'knet') {
      this.startInterval();
    } else if (this.paymentType === 'mastercard') {
      if (this.status === 'cancelled' || this.status === 'timeout') {
        this.handleMastercardAbort();
      } else {
        this.getResultIndicator();
      }

      // TODO: Uncomment to enable MyFatoorah
      // if (this.paymentId && !this.resultIndicator) {
      //   this.getMFTransactionStatus();
      // } else {
      //   this.getResultIndicator();
      // }
    } else {
      this.retrieveCharge();
    }
  }

  startInterval() {
    this.interval = setInterval(() => {
      this.retrieveCharge();
    }, 3000);
  }

  retrieveCharge() {
    this.authService.retrieveCharge(this.tap_id, this.paymentType).subscribe({
      next: (resp) => {
        if (resp?.status !== 'INITIATED') {
          clearInterval(this.interval);
          const message = `${resp?.status} - Transaction ID ${resp}`;
          this.storageService.setItem(SessionVariable.FAIL_ERROR_MSG, message);

          if (resp?.redirectUrl) {
            this.transferSignal.setMerchantRedirectUrl(resp.redirectUrl);
          }

          this.paymentStatus(resp);
        }
      },
      error: (err) => {
        this.storageService.setItem(SessionVariable.FAIL_ERROR_MSG, err?.message || err);
        this.checkoutService.showMessage('error', err?.message || err);
        this.router.navigate(['/secure-checkout/', this.orderId, 'payment', 'reject'], {
          state: { allowRetry: true },
        });
      },
      complete: () => {
        // Optional: logic when observable completes
      },
    });
  }

  getResultIndicator() {
    if (!this.resultIndicator || this.resultIndicator === 'undefined') {
      this.handleCardNotAdded(this.translationService.getLabelValue('CHECKOUT.ADD_CARD_FAILED'));
      return;
    }

    this.checkoutService.resultIndicatorStatus(this.resultIndicator).subscribe({
      next: (res) => {
        if (res?.id) {
          this.cardId = res.id;
          this.transfer.setSelectedPaymentMethod(res.id);

          if (this.orderId) {
            this.getPaymentInfo(this.orderId);
          } else {
            this.message = this.translationService.getLabelValue('CHECKOUT.ADD_CARD_SUCCESS');
            this.isSuccess = true;
            setTimeout(() => this.router.navigate(['/']), 1500);
          }
        } else {
          this.handleCardNotAdded(
            this.translationService.getLabelValue('CHECKOUT.ADD_CARD_FAILED')
          );
        }
      },
      error: (err) => {
        const errorCode = err?.errorCode;

        if (
          errorCode === MPGS_CARD_COMPLETION_IN_PROGRESS &&
          this.completionRetries < COMPLETION_IN_PROGRESS_RETRIES
        ) {
          this.completionRetries++;

          this.completionRetryTimeoutId = setTimeout(
            () => this.getResultIndicator(),
            COMPLETION_IN_PROGRESS_DELAY_MS
          );
          return;
        }

        if (
          errorCode === MPGS_CARD_NOT_CAPTURED ||
          errorCode === MPGS_CARD_REGISTRATION_FAILED ||
          errorCode === MPGS_CARD_COMPLETION_IN_PROGRESS
        ) {
          this.handleCardNotAdded(
            err?.message || this.translationService.getLabelValue('CHECKOUT.ADD_CARD_FAILED')
          );
          return;
        }

        this.storageService.setItem(SessionVariable.FAIL_ERROR_MSG, err?.message || err);
        this.checkoutService.showMessage('error', err?.message || err);
        this.router.navigate(['/secure-checkout/', this.orderId, 'payment', 'reject'], {
          state: { allowRetry: true },
        });
      },
    });
  }

  private handleMastercardAbort() {
    const key =
      this.status === 'timeout' ? 'CHECKOUT.ADD_CARD_TIMEOUT' : 'CHECKOUT.ADD_CARD_CANCELLED';

    this.handleCardNotAdded(this.translationService.getLabelValue(key));
  }

  private handleCardNotAdded(message: string) {
    this.checkoutService.showMessage('error', message);

    if (this.orderId) {
      this.redirectToPaymentPage();
    } else {
      this.router.navigate(['/']);
    }
  }

  // TODO: Uncomment to enable MyFatoorah
  // getMFTransactionStatus() {
  //   this.checkoutService.getTransactionStatusV2(this.paymentId).subscribe({
  //     next: (resText) => {
  //       let res = resText;
  //       try {
  //         if (typeof resText === 'string') res = JSON.parse(resText);
  //       } catch (e) {
  //         console.error('Error parsing MF transaction status:', e);
  //       }
  //
  //       if (typeof res === 'object') {
  //         if (res?.id || res?.creditCardId || res?.userCreditCardId) {
  //           this.transfer.setSelectedPaymentMethod(
  //             res?.id || res?.creditCardId || res?.userCreditCardId
  //           );
  //           this.getPaymentInfo(this.orderId);
  //           this.cardId = res.id || res.creditCardId || res.userCreditCardId;
  //         } else if (res?.status === 'SUCCESS' || res?.paymentStatus === 'CAPTURED') {
  //           // If it just successfully processed directly
  //           this.getPaymentInfo(this.orderId);
  //           this.cardId = res.id || res.creditCardId || res.userCreditCardId;
  //         } else {
  //           // For backwards compatibility and UI handling
  //           this.paymentStatus({
  //             status: 'VALID',
  //             object: 'verify_card',
  //             userCreditCardId: res?.id || res?.creditCardId,
  //           });
  //         }
  //       }
  //     },
  //     error: (err) => {
  //       this.storageService.setItem(SessionVariable.FAIL_ERROR_MSG, err?.message || err);
  //       this.checkoutService.showMessage('error', err?.message || err);
  //       this.router.navigate(['/secure-checkout/', this.orderId, 'payment', 'reject'], {
  //         state: { allowRetry: true },
  //       });
  //     },
  //   });
  // }

  validationStatus(requestId: any, validationURL: string, session: any) {
    this.loaderService.start();

    this.checkoutService.getCnetValidationStatus(requestId).subscribe({
      next: (res) => {
        this.cNetResponseStatus = res;
        const result = res?.result;

        const isPending =
          !res ||
          (typeof res === 'object' && Object.keys(res).length === 0) ||
          result === 'PENDING';

        if (isPending) {
          return;
        }

        if (result === 'SUCCESS') {
          clearInterval(this.intervalId);
          clearTimeout(this.timeoutId);
          this.storageService.setItem(SessionVariable.CREDIT_LIMIT, res?.creditLimit);
          this.loaderService.stop();
          this.payByCard(this?.cardId);
        } else if (result === 'AUTHENTICATION_REQUIRED') {
          this.handleAuthenticationRequired();
        } else if (result === 'REJECT') {
          clearInterval(this.intervalId);
          clearTimeout(this.timeoutId);
          this.loaderService.stop();

          const errorMessage: any = res?.errorMessage;

          if (errorMessage) {
            const title =
              this.textDirection === 'ltr'
                ? errorMessage?.enMessageTitle
                : errorMessage?.arMessageTitle;
            const msg =
              this.textDirection === 'ltr'
                ? errorMessage?.enMessageDescription
                : errorMessage?.arMessageDescription;
            this.router.navigate(['/rejection-error'], {
              relativeTo: this.activatedRoute,
              state: {
                errorType: 'CNET_REJECTION',
                errorMessage: msg || res?.rejectionReason,
                title: title,
              },
            });
          } else if (res?.rejectionReason) {
            this.router.navigate(['/rejection-error'], {
              relativeTo: this.activatedRoute,
              state: { errorType: '500', errorMessage: res?.rejectionReason },
            });
          } else {
            this.router.navigate(['/rejection-error'], {
              relativeTo: this.activatedRoute,
              state: { errorType: 500 },
            });
          }
        } else if (!res || !result || result === 'ERROR' || result === 'FAILED') {
          clearInterval(this.intervalId);
          clearTimeout(this.timeoutId);
          this.loaderService.stop();

          const errorMessageObj: any = res?.errorMessage;
          const errorCode = errorMessageObj?.errorCode || '500';

          const title = errorMessageObj
            ? this.textDirection === 'ltr'
              ? errorMessageObj?.enMessageTitle
              : errorMessageObj?.arMessageTitle
            : undefined;

          const msg = errorMessageObj
            ? this.textDirection === 'ltr'
              ? errorMessageObj?.enMessageDescription
              : errorMessageObj?.arMessageDescription
            : undefined;

          const state = { errorType: errorCode, errorMessage: msg || res?.rejectionReason, title };
          console.error('[CHECKOUT] CINET validation error:', errorCode);

          this.router.navigate(['../rejection-error'], { state, relativeTo: this.activatedRoute });
        }
      },
      error: (err) => {
        clearInterval(this.intervalId);
        clearTimeout(this.timeoutId);
        this.loaderService.stop();

        this.router.navigate(['/rejection-error'], {
          state: { errorType: '500', errorMessage: err?.message, allowRetry: true },
          relativeTo: this.activatedRoute,
        });
      },
      complete: () => {
        // Optional: logic when observable completes
      },
    });
  }

  private handleAuthenticationRequired(): void {
    clearInterval(this.intervalId);
    clearTimeout(this.timeoutId);
    this.loaderService.stop();

    this.cnetAuthSessionService.resolveCivilId();

    this.router.navigate(
      [`secure-checkout/${this.orderId}/cnet-authentication-existing-customer`],
      { relativeTo: this.activatedRoute }
    );
  }

  startValidateInterval(requestId: any, validationURL: string, session: any) {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.validationStatus(requestId, validationURL, session);

    this.intervalId = setInterval(() => {
      this.validationStatus(requestId, validationURL, session);
    }, 5000);

    this.timeoutId = setTimeout(() => {
      clearInterval(this.intervalId);
      this.loaderService.stop();
      this.router.navigate(['/rejection-error'], {
        relativeTo: this.activatedRoute,
        state: { errorType: 'TIMEOUT', allowRetry: true, backToLogin: true },
      });
    }, 60000);
  }

  paymentStatus(data: any) {
    if (this.paymentType == 'knet') {
      if (data?.status == 'SUCCESS') {
        this.navigateTo(data, true);
      } else {
        this.navigateTo(data, false);
      }
    } else {
      if (data?.status == 'VALID' && data?.object == 'verify_card') {
        this.status = 'success';
        if (this.orderId) {
          if (this.storageService.getItem(SessionVariable.AUTO_PAY)) {
            this.payByCard(data?.userCreditCardId);
          } else {
            setTimeout(() => {
              this.redirectToPaymentPage();
            }, 1500);
          }
        } else {
          setTimeout(() => {
            this.navigateTo(data, this.isSuccess);
          }, 1500);
        }
      } else if (data?.status == 'INVALID' && data?.object == 'verify_card') {
        this.message = data?.response?.message;
        this.isSuccess = false;
        this.storageService.setItem(SessionVariable.FAIL_ERROR_MSG, this.message);
        this.redirectToPaymentPage();

        this.toasterService.clear();
        this.toasterService.add({
          severity: 'error',
          detail: 'We are unable to add your card, please try again',
        });
      }
    }
  }

  redirectToPaymentPage() {
    this.router.navigate(['/secure-checkout/', this.orderId, 'payment-method']);
  }

  redirectToCheckoutPage(orderToken: string, isSuccess: boolean) {
    if (orderToken) {
      const state = isSuccess ? { queryParams: { orderToken } } : { allowRetry: true };

      this.router.navigate(
        ['/secure-checkout/', orderToken, 'payment', isSuccess ? 'success' : 'reject'],
        { state }
      );
    } else {
      this.router.navigate(['/consumer/billing']);
    }
  }

  navigateTo(data: any, status: boolean) {
    switch (data?.paymentType) {
      case 'DOWNPAYMENT':
        if (data?.redirectUrl) {
          this.transferSignal.setMerchantRedirectUrl(data?.redirectUrl);
        }

        if (data?.orderToken) {
          this.storageService.setItem(SessionVariable.ORDER_ID, data?.orderToken);
        }
        this.redirectToCheckoutPage(data?.orderToken, status);
        break;
      case 'FULLPAYMENT':
      case 'INSTALLMENT':
        this.router.navigate(['/consumer/dashboard'], { queryParams: { st: status } });
        break;
      case 'BULK_INSTALLMENT':
        this.router.navigate(['/consumer/dashboard'], { queryParams: { st: status } });
        break;
      default:
        this.router.navigate(['/consumer/dashboard'], { queryParams: { st: status } });
        break;
    }
  }

  payByCard(userCreditCardId: number) {
    this.acceptPolicy();

    const requestBody: any = {
      orderToken: this.storageService.getItem(SessionVariable.ORDER_ID),
      paymentPlanId:
        this.storageService?.getItem(SessionVariable.PAYMENT_PLAN_ID) ?? 'THREE_MONTHS',
      paymentMethod: 'CREDIT',
      paymentType: 'DOWNPAYMENT',
      creditCardId: userCreditCardId,
    };

    this.authService.orderProcess(requestBody).subscribe({
      next: (res) => {
        if (res?.redirectUrl) {
          this.transferSignal.setMerchantRedirectUrl(res.redirectUrl);
        }

        this.storageService.setItem(SessionVariable.PAYMENT_REFERENCE, res?.paymentReference);
        this.storageService.removeItem(SessionVariable.AUTO_PAY);

        if (GATEWAY_TOKEN_MISSING_CODES.includes(res?.code)) {
          this.handleCardNotAdded(
            this.translationService.getLabelValue('CHECKOUT.MY_FATOORAH_CARD_ERROR')
          );
          return;
        }

        if (
          res.orderStatus === 'CONFIRMED' ||
          res.paymentStatus === 'CAPTURED' ||
          res.paymentStatus === 'SUCCESS'
        ) {
          this.router.navigate(['/secure-checkout/', this.orderId, 'payment', 'success'], {
            queryParams: { orderToken: this.orderId },
          });
        } else {
          this.router.navigate(['/secure-checkout/', this.orderId, 'payment', 'reject'], {
            state: { allowRetry: true },
          });
        }
      },
      error: (err) => {
        this.storageService.setItem(SessionVariable.FAIL_ERROR_MSG, err?.message || err);
        this.router.navigate(['/secure-checkout/', this.orderId, 'payment', 'reject'], {
          state: { allowRetry: true },
        });
        this.storageService.removeItem(SessionVariable.AUTO_PAY);
      },
      complete: () => {
        // Optional: logic when observable completes
      },
    });
  }

  getPaymentInfo(orderId: any): void {
    if (!orderId) return;

    this.checkoutService.getPaymentInfo(orderId).subscribe({
      next: (resp) => {
        this.transfer.setOrderDetails(resp);
        // this.validateCnet(resp?.amount, resp?.id);
        if (!this.isPayLaterSelected()) {
          this.payByCard(this.cardId);
        } else {
          this.router.navigate(['/secure-checkout/', this.orderId, 'payment-method'], {
            relativeTo: this.activatedRoute,
          });
        }
      },
      error: (err) => {
        this.transfer.setIsLoading(false);
        this.storageService.setItem(SessionVariable.FAIL_ERROR_MSG, err?.message || err);
        this.checkoutService.showMessage('error', err?.message || err);
        this.router.navigate(['/secure-checkout/', this.orderId, 'payment', 'reject'], {
          state: { allowRetry: true },
        });
      },
    });
  }

  private acceptPolicy() {
    const orderId = this.storageService.getItem<string>(SessionVariable.ORDER_ID_FOR_CNET);

    if (!orderId) {
      return;
    }

    this.checkoutService.policyAcceptance(orderId).subscribe({
      error: (err) => console.error('Failed to accept policy:', err),
    });
  }

  ngOnDestroy() {
    if (this.completionRetryTimeoutId) {
      clearTimeout(this.completionRetryTimeoutId);
    }

    this.transferSignal.setHeader(true);
    this.transferSignal.setDarkHeader(true);
  }

  private isPayLaterSelected(): boolean {
    return (
      this.transfer.isPayLaterSelected() ||
      this.storageService.getItem<boolean>(SessionVariable.IS_PAY_LATER_SELECTED) === true
    );
  }
}
